import { clearStorageKeepingDeviceTerms } from '../store/deviceTermsSlice';
import { loadTokenBalance, clearTokenBalanceCache } from "../api/usage";
import { clearFeatureGatesCache } from '../api/network_calls';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Amplify } from 'aws-amplify';
import { fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth';
import amplifyconfig from '../amplifyconfiguration.json';
import amplifyconfigDev from '../amplifyconfiguration.dev.json';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { set_user, refresh_stories, set_notifications, update_tokens, update_billing_account } from '../store/appSlice';

// Configure Amplify based on environment
const config = process.env.NODE_ENV === 'development' ? amplifyconfigDev : amplifyconfig;
Amplify.configure(config);

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: (options?: { global?: boolean }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => ({
    email: state.app.email,
    userId: state.app.userId,
    tokens: state.app.tokens,
    character_tokens: state.app.character_tokens,
    is_platinum: state.app.is_platinum,
    is_premium: state.app.is_premium,
    email_verified: state.app.email_verified,
  }), shallowEqual);
  const [isLoading, setIsLoading] = useState(true);
  const currentTokens = useRef(user.tokens);
  currentTokens.current = user.tokens;

  // Keep the shared account balance current as cloud jobs settle or monthly
  // credits expire, and recover from a transient failure during sign-in.
  // Older deployments retain the Cognito total until the wallet endpoint exists.
  useEffect(() => {
    if (isLoading || !user.userId) return;
    const userId = user.userId;
    const controller = new AbortController();
    let loading = false;
    const refreshBalance = async () => {
      if (loading || controller.signal.aborted || document.hidden) return;
      loading = true;
      try {
        const wallet = await loadTokenBalance(controller.signal, { expectedTotal: currentTokens.current });
        if (controller.signal.aborted) return;
        if (wallet.available && Number.isSafeInteger(wallet.total) && wallet.total !== currentTokens.current) {
          dispatch(update_tokens({ tokens: wallet.total! }));
        }
        // Billing metadata is authoritative even when the balance has not changed.
        // Missing metadata means an older deployment, so retain its legacy values.
        if (wallet.membership || wallet.trial) {
          dispatch(update_billing_account({
            userId,
            ...(wallet.membership ? {
              tier: wallet.membership.tier,
              web_subscription: wallet.membership.webSubscription,
            } : {}),
            ...(wallet.trial ? {
              is_trial: wallet.trial.active,
              has_used_trial: wallet.trial.used,
              trial_end_date: wallet.trial.endsAt ? Date.parse(wallet.trial.endsAt) : null,
            } : {}),
          }));
        }
      } catch { /* Keep the last known balance and retry on the next refresh. */ }
      finally { loading = false; }
    };
    void refreshBalance();
    const timer = window.setInterval(refreshBalance, 120000);
    window.addEventListener('focus', refreshBalance);
    document.addEventListener('visibilitychange', refreshBalance);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshBalance);
      document.removeEventListener('visibilitychange', refreshBalance);
    };
  }, [isLoading, user.userId, dispatch]);

  const fetchUser = async () => {
    try {
      const [userAttributes, currentUser] = await Promise.all([
        fetchUserAttributes(),
        getCurrentUser(),
      ]);

      const {
        email,
        ['custom:TokenCount']: tokens = 0,
        ['custom:IsPlatinum']: is_platinum = 'false',
        ['custom:IsPremium']: is_premium = 'false',
        ['custom:CharacterTokenCount']: character_tokens = 0,
        ['custom:WebSubscription']: web_subscription = 'false',
        ['custom:IsTrial']: is_trial = 'false',
        ['custom:TrialEndDate']: trial_end_date = '',
        ['custom:TrialUsed']: has_used_trial = 'false',
      } = userAttributes;

      const { username: userId } = currentUser;

      dispatch(
        set_user({
          email: email || null,
          userId: userId || null,
          tier: null,
          tokens: parseInt(tokens as string) || 0,
          character_tokens: parseInt(character_tokens as string) || 0,
          is_platinum: is_platinum === 'true',
          is_premium: is_premium === 'true',
          is_trial: is_trial === 'true',
          trial_end_date: trial_end_date ? parseInt(trial_end_date as string) : null,
          has_used_trial: has_used_trial === 'true',
          email_verified: true,
          web_subscription: web_subscription === 'true', // Always true for web users who are authenticated
        })
      );
      
      dispatch(
        refresh_stories({
          stories: [],
          last_refreshed_at: null,
          last_story_edited_at: null,
        })
      );
      
      dispatch(
        set_notifications({
          story_updates: true,
          fable_updates: true,
        })
      );
    } catch (error) {
      console.error('AuthContext: User not authenticated:', error);
      dispatch(
        set_user({
          email: null,
          userId: null,
          tier: null,
          tokens: 0,
          character_tokens: 0,
          is_platinum: false,
          is_premium: false,
          is_trial: false,
          trial_end_date: null,
          has_used_trial: false,
          email_verified: null,
          web_subscription: false,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };



  const logout = async (options?: { global?: boolean }) => {
    clearTokenBalanceCache();
    clearFeatureGatesCache();
    try {
      // Clear user data first
      dispatch(
        set_user({
          email: null,
          userId: null,
          tier: null,
          tokens: 0,
          character_tokens: 0,
          is_platinum: false,
          is_premium: false,
          is_trial: false,
          trial_end_date: null,
          has_used_trial: false,
          email_verified: null,
          web_subscription: false,
        })
      );
      
      // Clear stories
      dispatch(
        refresh_stories({
          stories: [],
          last_refreshed_at: null,
          last_story_edited_at: null,
        })
      );
      
      // Clear notifications
      dispatch(
        set_notifications({
          story_updates: true,
          fable_updates: true,
        })
      );
      
      // Sign out from Amplify with global sign out to clear all tokens
      await signOut({ global: options?.global ?? true });
      
      // Clear any cached data in localStorage/sessionStorage
      clearStorageKeepingDeviceTerms();
      
      // Force a page reload to clear any cached state
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if signOut fails, clear everything and reload
      clearStorageKeepingDeviceTerms();
      window.location.reload();
    }
  };

  const refreshUser = async () => {
    clearTokenBalanceCache();
    clearFeatureGatesCache();
    await fetchUser();
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      console.log('AuthContext: Loading timeout reached, setting loading to false');
      setIsLoading(false);
    }, 10000); // 10 second timeout

    fetchUser().finally(() => {
      clearTimeout(timeoutId);
    });

    return () => clearTimeout(timeoutId);
  }, []);

  const value: AuthContextType = {
    isLoading,
    isAuthenticated: !!user.userId,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
